import React from 'react';
import { Link } from 'react-router-dom';
import { FaHome, FaChevronRight, FaLock, FaFingerprint, FaIdCard, FaCheckCircle, FaArrowRight, FaPhone, FaDoorOpen, FaKey, FaMobileAlt } from 'react-icons/fa';
import Navigation from '../components/Navigation';
import TopBar from '../components/TopBar';
import Footer from '../components/Footer';

const AccessControlPage = () => {
    const services = [
        {
            icon: <FaIdCard size={24} />,
            title: 'Card Access Systems',
            description: 'Proximity card and smart card readers for secure building and room access management.',
            gradient: 'from-gray-700 to-gray-900'
        },
        {
            icon: <FaFingerprint size={24} />,
            title: 'Biometric Access',
            description: 'Fingerprint, facial recognition, and retina scanning for highest-security applications.',
            gradient: 'from-purple-600 to-indigo-600'
        },
        {
            icon: <FaMobileAlt size={24} />,
            title: 'Mobile Credentials',
            description: 'Smartphone-based access using Bluetooth and NFC for convenient, keyless entry.',
            gradient: 'from-blue-600 to-cyan-600'
        },
        {
            icon: <FaDoorOpen size={24} />,
            title: 'Door Controllers',
            description: 'Intelligent door controllers with anti-tailgating, time schedules, and integration options.',
            gradient: 'from-teal-500 to-green-500'
        },
        {
            icon: <FaKey size={24} />,
            title: 'Visitor Management',
            description: 'Digital visitor sign-in systems with badge printing and pre-registration capabilities.',
            gradient: 'from-orange-500 to-amber-500'
        },
        {
            icon: <FaLock size={24} />,
            title: 'Cloud Management',
            description: 'Cloud-based access control platforms for remote management and real-time monitoring.',
            gradient: 'from-rose-500 to-red-500'
        }
    ];

    const benefits = [
        'Enhanced facility security',
        'Detailed access logs',
        'Remote management',
        'Integration with HR systems',
        'Scalable architecture',
        'Compliance support'
    ];

    return (
        <div className="font-sans">
            <TopBar />
            <Navigation />

            {/* Hero Section */}
            <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-24 overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    }}></div>
                </div>

                <div className="absolute top-0 right-0 w-96 h-96 bg-gray-500 rounded-full filter blur-3xl opacity-20 translate-x-1/2 -translate-y-1/2"></div>
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl opacity-20 -translate-x-1/2 translate-y-1/2"></div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <nav className="flex items-center gap-2 text-sm text-gray-300 mb-8">
                        <Link to="/" className="flex items-center gap-1 hover:text-white transition-colors">
                            <FaHome size={14} />
                            <span>Home</span>
                        </Link>
                        <FaChevronRight size={10} className="text-gray-500" />
                        <span className="text-blue-400">Access Control</span>
                    </nav>

                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                                Smart <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">Access Control</span> Solutions
                            </h1>
                            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                                Control who enters your facilities with intelligent access management systems designed for security and convenience.
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-gray-700 to-gray-900 text-white rounded-xl font-semibold hover:shadow-lg transition-all duration-300 border border-gray-600">
                                    Get Started
                                    <FaArrowRight size={14} />
                                </Link>
                                <a href="tel:+12014099845" className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/30 text-white rounded-xl font-semibold hover:bg-white/10 transition-all duration-300">
                                    <FaPhone size={14} />
                                    Call Us Now
                                </a>
                            </div>
                        </div>
                        <div className="relative">
                            <div className="bg-gradient-to-br from-gray-700/20 to-blue-600/20 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
                                <img
                                    src="https://images.unsplash.com/photo-1558002038-1055907df827?w=600&h=400&fit=crop"
                                    alt="Access Control"
                                    className="w-full h-80 object-cover rounded-xl shadow-2xl"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Services Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <span className="inline-block px-4 py-2 bg-gray-100 text-gray-700 rounded-full text-sm font-semibold mb-4">Our Services</span>
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Complete Access Control Solutions</h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            From card readers to biometrics, we provide comprehensive access management for any facility
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {services.map((service, index) => (
                            <div
                                key={index}
                                className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-gray-300"
                            >
                                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${service.gradient} flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform duration-300`}>
                                    {service.icon}
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                                <p className="text-gray-600 leading-relaxed">{service.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Benefits Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-blue-50">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <div>
                            <img
                                src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=600&h=500&fit=crop"
                                alt="Modern Office"
                                className="w-full h-auto rounded-2xl shadow-2xl"
                            />
                        </div>
                        <div>
                            <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4">Why Choose Us</span>
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Secure Access, Simplified</h2>
                            <p className="text-lg text-gray-600 mb-8">
                                Our access control systems provide the perfect balance of security and convenience for your organization.
                            </p>
                            <div className="grid grid-cols-2 gap-4">
                                {benefits.map((benefit, index) => (
                                    <div key={index} className="flex items-center gap-3">
                                        <FaCheckCircle className="text-blue-600 flex-shrink-0" size={20} />
                                        <span className="text-gray-700 font-medium">{benefit}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-gray-800 to-gray-900">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Ready to Secure Your Facility?</h2>
                    <p className="text-xl text-gray-300 mb-8">
                        Get a customized access control solution designed for your specific requirements.
                    </p>
                    <div className="flex flex-wrap justify-center gap-4">
                        <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-white text-gray-800 rounded-xl font-semibold hover:shadow-lg transition-all duration-300">
                            Request Free Consultation
                            <FaArrowRight size={14} />
                        </Link>
                        <a href="tel:+12014099845" className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white rounded-xl font-semibold hover:bg-white/10 transition-all duration-300">
                            <FaPhone size={14} />
                            Call Now
                        </a>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default AccessControlPage;
